import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import { Button } from 'devextreme-react/button'
import { formatNumber } from "devextreme/localization"
import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box'
import { BaseForm, TextInput } from 'components'
import { Form, Field } from 'formik'
import { SelectInput } from 'components/forms'
import { AccountLookup } from 'components/forms/lookups/account-lookup'
import { keyboardControl } from 'helpers'
import { useIxFocusElement } from 'hooks'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'slices'
import { setBookingUrl, setBookingBlockUrl, setInitialValues, setAccountRowData,
    setactiveBalanceButton, setBalanceLabel, setActiveContraAccount, setActiveRevertedBookings, setActiveDivRef } from 'slices/statement-filters-slice'

import { setMaxLabelWidth } from 'helpers/set-max-label-width'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import client from 'api'
import { IxLookupProvider } from 'contexts/lookup-context'
import { useIxOnScreen } from 'hooks/use-on-screen'
import './statement-filters.scss'

interface IAutoUpdateForm {
    values: any,
    itemsData: any[],
    lastFocusedElement: any,
    setInitialValues: any,
    divRef: any
}

const AutoUpdateForm = ({ values, setInitialValues, lastFocusedElement, itemsData, divRef }: IAutoUpdateForm) => {
    const dispatch = useDispatch()
    const activeDivRef = useSelector((state: RootState) => state.statementFilters.activeDivRef)

    useEffect(() => {
        dispatch(setInitialValues({ year: values.year, accountId: values.ACCOUNT, bookingNr: values.bookingsBlock }))
    },[values, setInitialValues, dispatch])
 
    useIxFocusElement(values, itemsData, lastFocusedElement, activeDivRef, divRef)

    return null
}

export const StatementFilters = () => {
    const dispatch = useDispatch()
    const [yearsData, setYearsData] = useState<any[]>([])
    const { t } = useTranslation()
    const { initialValues, accountRowData, allRowsSum, activeBalanceButton,
        balanceLabel, activeRevertedBookings, activeContraAccount} = useSelector((state: RootState) => state.statementFilters)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const [responsive, setResponsive] = useState<boolean>(false)

    const ref = useRef(null)
    const onScreen = useIxOnScreen(ref)
    
    useEffect(() => {
        if (onScreen) {
            const formElement: any = ref.current

            if (formFieldType === 'label-inside-max-field-width' || formFieldType === 'label-left-max-field-width') {
                setTimeout(() => {
                    setMaxLabelWidth(formElement)
                }, 500) 
            }
            else {
                formElement?.style.removeProperty('--max-labels-width')
            }
        }

    },[onScreen, formFieldType])

    const itemsData = useMemo(() => ([
        {name: 'ACCOUNT', index: 1, type: 'field'},
        {name: 'year', index: 1, type: 'field'},
        {name: 'bookingsBlock', index: 0, type: 'field'},
        {name: 'totalBalance', index: 0, type: 'field'},

        {name: t('global:label.accountStatement.net'), index: 0, type: 'button'},
        {name: t('global:label.accountStatement.gross'), index: 0, type: 'button'},
        {name: t('global:label.accountStatement.standard'), index: 0, type: 'button'},
        {name: t('global:label.accountStatement.onlyBalance'), index: 0, type: 'button'},
        {name: t('global:label.accountStatement.contraAccount'), index: 0, type: 'button'},
        {name: t('global:label.accountStatement.revertedBookings'), index: 0, type: 'button'},
    ]),[t])

    const lastFocusedElement = useRef<any>(null)
    const onKeyboardControl = keyboardControl(itemsData, t, ref, lastFocusedElement)

    useEffect(() => {
        if (onScreen) {
            const filterElement: HTMLElement = (ref.current as any).closest('.dx-drawer-panel-content') || (ref.current as any).closest('.dx-drawer-content')
            filterElement?.addEventListener('keydown', onKeyboardControl)

            return () => {
                filterElement?.removeEventListener('keydown', onKeyboardControl)
            }
        }
                
    },[onKeyboardControl, onScreen])

     useEffect(() => {
        const filterElement: HTMLElement = (ref.current as any).closest('.dx-drawer-panel-content') || (ref.current as any).closest('.dx-drawer-content')

        const setPrevFocusedElement = (e: any) => {
            const filterButton = e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]')
            dispatch(setActiveDivRef(e.target.closest('.dx-drawer-content') || e.target.closest('.dx-drawer-panel-content')))
            
            if (e.target.tagName === 'DIV' && !e.target.className.includes('dropdowneditor') && !e.target.className.includes('button') && !e.target.closest('.dx-radiobutton') && filterButton) {
                setTimeout(() => {
                    e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]').focus()
                    e.target.closest('.dx-drawer-panel-content')?.querySelector('[aria-label="filter"]').classList.remove('dx-state-focused')
                }, 0)
            }
            
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'I' || e.target.className.includes('dropdowneditor')) {
                lastFocusedElement.current = e.target
                filterElement?.querySelector('.dx-state-focused.dx-radiobutton')?.classList.remove('dx-state-focused')
                filterElement?.querySelector('.dx-checkbox.dx-state-focused')?.classList.remove('dx-state-focused')
            }
        }
        filterElement?.addEventListener('mousedown', setPrevFocusedElement)

        return () => {
            filterElement?.removeEventListener('mousedown', setPrevFocusedElement)
        }
    }, [dispatch])

    useEffect(() => {
        initialValues.bookingNr && accountRowData?.BuKrNr && initialValues.year
        ? dispatch(setBookingBlockUrl(`/api/ixweb/bublock/?BUNR=${initialValues.bookingNr}&BUKRNR=${accountRowData.BuKrNr}&WJAHR=${initialValues.year}`))
        : dispatch(setBookingBlockUrl(''))

    },[initialValues.bookingNr, accountRowData?.BuKrNr, initialValues.year, dispatch])


    useEffect(() => {
        if (accountRowData?.AccountId && initialValues.year) {
            const accountId = `konto_id=${accountRowData.AccountId}`
            const year = `year=${initialValues.year}`
            const showRevertedBookings = `ShowStorno=${activeRevertedBookings}`
            const balanceOption = `BRUTTONETTOSTANDARDOPTION=${activeBalanceButton}`
            const showContraAccount = `SHOWGEGENKONTO=${activeContraAccount}`
            const yearId = yearsData.find((year: any) => year.JAHR === initialValues.year)?.HDR_ID

            dispatch(setBookingUrl(
                activeBalanceButton === 3
                    ? `api/ixweb/ktosld/?action=eql&KontoId=${accountRowData.AccountId}&WJahrId=${yearId}&fields=soll,haben,saldo`
                    : `/api/ixweb/xsp-func/?${accountId}&${year}&${showRevertedBookings}&${balanceOption}&SYSSETTINGUSENEWVERSION=0&${showContraAccount}`
            ))
        }
        else { dispatch(setBookingUrl('')) }

    },[accountRowData?.AccountId, initialValues.year, activeBalanceButton, activeRevertedBookings, activeContraAccount, yearsData, dispatch])

    useEffect(() => {
        accountRowData?.BuKrNr ?
            // client.get(`/api/ixweb/wjahr/?bukreisid=${accountRowData.BuKrNr}`).then((res: any) => {
                // TO DO !!!!
                client.get(`/api/ixweb/bukreis/?action=eql&bukrnr=${accountRowData.BuKrNr}`).then((res: any) => {
                    const bukreisId = res.data[0].HDR_ID
                    // client.get(`/api/ixweb/wjahr/?min_bukreisid=${bukreisId}&max_bukreisid=${bukreisId + 1}`).then((res: any) => {
                    client.get(`/api/ixweb/wjahr/?min_bukreisid=${bukreisId}&max_bukreisid=${bukreisId}`).then((res: any) => {
                        setYearsData(res.data.reverse())
                    })

                })
        : setYearsData([])

    },[accountRowData?.BuKrNr])

    const balanceTypes: any = useMemo(() => (
        {
            0: 'global:label.accountStatement.netBalance',
            1: 'global:label.accountStatement.grossBalance',
            2: 'global:label.accountStatement.balance',
            3: 'global:label.accountStatement.balance'
        }
    ),[])

    const buttons = useMemo(() => (
        [
            {name: t('global:label.accountStatement.net'), id: 0},
            {name: t('global:label.accountStatement.gross'), id: 1},
            {name: t('global:label.accountStatement.standard'), id: 2},
            {name: t('global:label.accountStatement.onlyBalance'), id: 3},
            {name: t('global:label.accountStatement.contraAccount'), id: 4},
            {name: t('global:label.accountStatement.revertedBookings'), id: 5},
        ]
    ),[t])

    const OPTIONS = useMemo(() => ({
        years: yearsData.map((year: any) => (
            {name: year.JAHR, value: year.JAHR}
        ))

    }),[yearsData])

    const onActiveCancellationButton = useCallback((e: any) => {
        const number = Number(e.element.id)
        const button = {
            4: {
                activeButton: activeContraAccount,
                setActiveButton: (e: any) => dispatch(setActiveContraAccount(e))
            },
            5: {
                activeButton: activeRevertedBookings,
                setActiveButton: (e: any) => dispatch(setActiveRevertedBookings(e))
            }
        } as any

        button[number].activeButton === 0
            ? button[number].setActiveButton(1)
            : button[number].setActiveButton(0)

    },[activeContraAccount, activeRevertedBookings, dispatch])

    const onActiveBalanceButton = useCallback((e: any) => {
        dispatch(setactiveBalanceButton(Number(e.element.id)))
        dispatch(setBalanceLabel(balanceTypes[e.element.id]))

    },[balanceTypes, dispatch])


    const numberFormatter = useCallback((value: any, currency?: string) => (
        formatNumber(value, {
            type: 'currency',
            precision: 2,
            currency: currency || 'EUR',
        })
    ),[])

    const formik = useMemo(() => ({
        initialValues: {
            ACCOUNT: initialValues.accountId,
            year: initialValues.year,
            totalBalance: numberFormatter(allRowsSum?.balance),
            bookingsBlock: initialValues.bookingNr
        },
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },

    }),[initialValues.accountId, initialValues.bookingNr, initialValues.year, allRowsSum?.balance, numberFormatter])

    return (
        <div
            className={`statement-filters ${responsive ? 'statement-filters-responce' : ''}`}
            ref={ref}
        >
            <IxLookupProvider>
                <BaseForm {...formik} >
                    {({ values }: any) => (
                        <Form id='statement-filters'>
                            <AutoUpdateForm values={values} setInitialValues={setInitialValues} lastFocusedElement={lastFocusedElement} itemsData={itemsData} divRef={ref}/>
                            <ResponsiveBox
                                onOptionChanged={(e) => { setResponsive(e.element.offsetWidth < 705) }}
                            >
                                <Row />
                                <Row />

                                <Col/>
                                <Col/>
                                <Col/>
                                <Col/>

                                <Item>
                                    <Location row={0} col={0}/>
                                    <Field
                                        as={AccountLookup}
                                        name='ACCOUNT'
                                        label={t('global:label.accountType.account')}
                                        onSelectionChanged={(data: any) => dispatch(setAccountRowData(data))}
                                    />
                                </Item>

                                <Item>
                                    <Location row={0} col={1} />
                                    <Field
                                        as={SelectInput}
                                        name='year'
                                        opts={OPTIONS.years}
                                        label={t('global:label.accountStatement.year')}
                                    />
                                </Item>

                                <Item>
                                    <Location row={0} col={2}/>
                                    <Field
                                        as={TextInput}
                                        name='bookingsBlock'
                                        label={t('global:label.accountStatement.bookingsBlock')}
                                    />
                                </Item>

                                <Item>
                                    <Location row={0} col={3} />
                                    <Field
                                        as={TextInput}
                                        name='totalBalance'
                                        readOnly={true}
                                        label={t(balanceLabel)}
                                    />
                                </Item>

                                <Item>
                                    <Location row={1} col={0} colspan={2}/>
                                    <div className='balance-buttons'>
                                    {
                                        buttons.map((button) => (
                                            button.id < 4 &&
                                            <Button
                                                text={button.name}
                                                type={activeBalanceButton === button.id ? 'success' : 'normal'}
                                                onClick={onActiveBalanceButton}
                                                id={`${button.id}`}
                                                key={button.id}
                                            />
                                        ))
                                    }
                                    </div>
                                </Item>

                                <Item>
                                    <Location row={1} col={3} />
                                    <div className='cancellation-buttons'>
                                    {
                                        buttons.map((button) => (
                                            button.id > 3 &&
                                            <Button
                                                text={button.name}
                                                type={(button.id === 4 ? activeContraAccount : activeRevertedBookings) === 1 ? 'success' : 'normal'}
                                                onClick={onActiveCancellationButton}
                                                id={`${button.id}`}
                                                key={button.id}
                                            />
                                        ))
                                    }
                                    </div>
                                </Item>

                            </ResponsiveBox>
                        </Form>
                    )}
                </BaseForm>
            </IxLookupProvider>
        </div>
    )
}
